import packageData from "../../content/course-v5/packages/K01.json";
import coursesData from "../../content/course-v5/courses.json";
import artifactsData from "../../content/course-v5/production/K01/artifacts.json";
import casesData from "../../content/course-v5/production/K01/cases.json";
import narrationData from "../../content/course-v5/production/K01/narration.json";
import scenesData from "../../content/course-v5/production/K01/scenes.json";

export type K01Track = "school" | "el-etg" | "own-idea";
export type CourseId = "general" | "el-etg";

export interface Course {
  id: "general" | "el-etg" | "veterinary";
  shortLabel: string;
  title: string;
  catalogDescription: string;
  description: string;
  status: "available" | "planned";
  transferTrack: K01Track | null;
  visualKey: "general" | "el-etg" | "veterinary";
}

export interface K01Scene {
  id: string;
  studioId: string;
  order: number;
  title: string;
  guidedMinutes: number;
  purpose: string;
  learnerPromise: string;
  actionLabel: string;
  narrationId: string;
  interactionStartBySeconds: number;
  interaction: {
    operation: string;
    instruction: string;
    controls: string[];
    visibleConsequence: string;
    trackMode: string;
    artifactId: string;
    artifactWrites: string[];
    controlRules?: { allowNoFeatures?: boolean; noFeaturesConsequence?: string };
    controlOptions?: Record<string, string[]>;
    controlMetaphor?: string;
    mobilePattern: { maxSimultaneousControls: number; sequence: string; steps: string[][]; pinnedContext: string[] };
    learnerLabel?: string;
    guidedMode?: { default: string; advanced: string };
  };
  motion: {
    type: string;
    causal: boolean;
    stateChanges: string[];
    reducedMotionEquivalent: string;
    visualEmphasis?: string;
    visualSemantics?: string;
  };
  feedback: {
    effective: string;
    partial: string;
    misconception: string;
    retry: string;
    next: string;
  };
  sourceIds: string[];
}

export interface K01NarrationSegment {
  id: string;
  sceneId: string;
  audioFile: string;
  status: string;
  targetSeconds: number;
  spokenLine: string;
  transcript: string;
  captionCues: string[];
  audioRevisionStatus?: "current" | "regeneration-required";
}

export interface K01SystemCase {
  id: string;
  title: string;
  surface: string;
  mechanism: "ai" | "ordinary-code" | "automation" | "human" | "unknown";
  shownEvidence: string[];
  responsibleHuman: string;
}

export interface K01TrainingCard {
  id: string;
  title: string;
  symbol: string;
  wordCountBand: string;
  label: string;
}

export interface K01TransferCase {
  id: string;
  title: string;
  goal: string;
  mechanismOptions: string[];
  newQuestion: string;
}

export interface K01ArtifactField {
  id: string;
  type: string;
  prompt: string;
  options?: string[];
  sentenceStarters?: string[];
}

export interface K01ArtifactTemplate {
  id: string;
  title: string;
  type: string;
  learnerPromise: string;
  teenCore: {
    choicePrompt: string;
    choiceOptions: Array<{ id: string; label: string }>;
    editPrompt: string;
    editPlaceholder: string;
  };
  fields: K01ArtifactField[];
  completionRule: string;
  feedbackRule: string;
}

export interface K01Studio {
  id: string;
  title: string;
  guidedMinutes: number;
  learningGoal: string;
  requiredTerms: string[];
  artifactId: string;
}

interface K01PackageData {
  id: string;
  title: string;
  promise: string;
  guidedMinutes: number;
  studios: K01Studio[];
}

interface K01ScenesData {
  scenes: K01Scene[];
}

interface K01NarrationData {
  segments: K01NarrationSegment[];
}

interface K01CasesData {
  systemCases: K01SystemCase[];
  trainingCards: K01TrainingCard[];
  transferCases: Record<K01Track, K01TransferCase[]>;
  driverScenarios: Array<{ id: string; title: string; description: string; allowedOutcomes: string[]; forbidden: string[] }>;
}

interface K01ArtifactsData {
  templates: K01ArtifactTemplate[];
}

interface CoursesData {
  schemaVersion: number;
  defaultCourseId: CourseId;
  courses: Course[];
}

export const k01Package = packageData as K01PackageData;
export const k01Scenes = (scenesData as K01ScenesData).scenes;
export const k01Narration = (narrationData as K01NarrationData).segments;
export const k01Cases = casesData as K01CasesData;
export const k01Artifacts = (artifactsData as K01ArtifactsData).templates;
export const courses = (coursesData as CoursesData).courses;
export const availableCourses = courses.filter((course): course is Course & { id: CourseId; status: "available" } => course.status === "available" && (course.id === "general" || course.id === "el-etg"));
export const defaultCourseId = (coursesData as CoursesData).defaultCourseId;
export const courseById = new Map<string, Course>(courses.map((course) => [course.id, course]));

export const k01SceneById = new Map(k01Scenes.map((scene) => [scene.id, scene]));
export const k01NarrationById = new Map(k01Narration.map((segment) => [segment.id, segment]));
export const k01ArtifactById = new Map(k01Artifacts.map((artifact) => [artifact.id, artifact]));

export const k01SceneNumber = (sceneId: string): number => k01Scenes.findIndex((scene) => scene.id === sceneId) + 1;

export function getStudioScenes(studioId: string): K01Scene[] {
  return k01Scenes.filter((scene) => scene.studioId === studioId);
}

export function getTransferCases(track: K01Track): K01TransferCase[] {
  return k01Cases.transferCases[track] ?? [];
}

export const k01DefaultCaseDeck = [
  "SYS-CALC-01",
  "SYS-CLASSIFIER-01",
  "SYS-CHAT-UNKNOWN-01",
  "SYS-DOC-RULE-01",
  "SYS-HUMAN-01",
  "SYS-RECOMMEND-01",
].map((id) => k01Cases.systemCases.find((item) => item.id === id)).filter((item): item is K01SystemCase => Boolean(item));
